import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * InventoryDashboardTab - Inventory insights and analytics
 * Uses table-based layout matching Financial Hub Dashboard
 */
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FaGem, FaDollarSign, FaChartLine, FaClock, FaExclamationCircle, FaBolt, FaCheckCircle, FaCertificate, FaSync, FaEye, FaBullhorn, FaShieldAlt } from 'react-icons/fa';
import { PieChart, Pie, BarChart, Bar, Cell, ResponsiveContainer, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { RepriceDiamondModal } from '../../../../components/Inventory/RepriceDiamondModal';
import { QuickActionModal } from '../../../../components/Inventory/QuickActionModal';
import { DiamondDetailModal } from '../../../../components/DiamondDetailModal';
import { apiClient } from '../../../../utils/apiClient';
import { notify } from '../../../../utils/notifications';
import mockDataService from '../../../../services/mockDataService';
import { useDashboardPreferences } from '../../../../hooks/useDashboardPreferences';
import DashboardSettingsPanel from '../../../../components/Dashboard/DashboardSettingsPanel';
import { INVENTORY_DASHBOARD_SECTIONS } from '../../../../types/dashboardPreferences';
import { Button, TableContainer, Table, Th, Td, Tr } from '../../FinancialHub/SharedComponents';
const Container = styled.div `
  padding: 0;
`;
const HeaderActions = styled.div `
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
`;
const Section = styled.div `
  margin-bottom: 2rem;
`;
const SectionHeader = styled.div `
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid ${props => props.$color || 'var(--border-color)'};
`;
const SectionTitle = styled.h3 `
  margin: 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;
const SectionBadge = styled.span `
  background: ${props => props.$color || '#6b7280'};
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
`;
const ActionButtons = styled.div `
  display: flex;
  gap: 0.5rem;
`;
const ActionButton = styled.button `
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;

  ${props => {
    switch (props.$variant) {
        case 'primary':
            return `
          background: rgba(184, 134, 11, 0.1);
          border-color: #B8860B;
          color: #B8860B;
          &:hover { background: rgba(184, 134, 11, 0.2); }
        `;
        case 'success':
            return `
          background: rgba(16, 185, 129, 0.1);
          border-color: #10b981;
          color: #10b981;
          &:hover { background: rgba(16, 185, 129, 0.2); }
        `;
        case 'danger':
            return `
          background: rgba(239, 68, 68, 0.1);
          border-color: #ef4444;
          color: #ef4444;
          &:hover { background: rgba(239, 68, 68, 0.2); }
        `;
        default:
            return `
          background: var(--bg-tertiary);
          border-color: var(--border-color);
          color: var(--text-secondary);
          &:hover { background: var(--bg-secondary); }
        `;
    }
}}
`;
const PriceCell = styled.span `
  font-weight: 600;
  color: ${props => props.$warning ? '#ef4444' : 'var(--accent-gold)'};
`;
const DaysBadge = styled.span `
  display: inline-block;
  padding: 0.125rem 0.5rem;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => {
    if (props.$days > 90)
        return '#dc262615';
    if (props.$days > 60)
        return '#f59e0b15';
    return '#10b98115';
}};
  color: ${props => {
    if (props.$days > 90)
        return '#dc2626';
    if (props.$days > 60)
        return '#f59e0b';
    return '#10b981';
}};
`;
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;
const StatCard = styled.div `
  background: linear-gradient(135deg, ${props => props.$color}15 0%, ${props => props.$color}05 100%);
  border: 1px solid ${props => props.$color}30;
  border-left: 4px solid ${props => props.$color};
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 24px ${props => props.$color}20;
  }
`;
const StatLabel = styled.div `
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-tertiary);
  font-weight: 600;
  margin-bottom: 0.5rem;
`;
const StatValue = styled.div `
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  font-family: 'JetBrains Mono', monospace;
`;
const StatSubtext = styled.div `
  font-size: 0.75rem;
  color: var(--text-secondary);
  margin-top: 0.5rem;
`;
const ChartsContainer = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;
const ChartCard = styled.div `
  background: var(--bg-tertiary);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 1.5rem;
`;
const ChartTitle = styled.h3 `
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: var(--text-secondary);
  margin: 0 0 1rem 0;
`;
const EmptyState = styled.div `
  text-align: center;
  padding: 4rem 2rem;
  color: var(--text-tertiary);

  svg {
    font-size: 4rem;
    color: #8b5cf6;
    margin-bottom: 1rem;
  }

  h3 {
    color: var(--text-primary);
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;
const AllCaughtUp = styled.div `
  text-align: center;
  padding: 3rem 2rem;
  color: var(--text-tertiary);
  background: var(--bg-secondary);
  border-radius: 8px;
  border: 1px solid var(--border-color);

  svg {
    font-size: 3rem;
    color: #10b981;
    margin-bottom: 1rem;
  }

  h3 {
    color: var(--text-primary);
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;
const SHAPE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4'];
const AGING_COLORS = ['#10b981', '#fbbf24', '#f59e0b', '#ef4444'];
const InventoryDashboardTab = ({ onNavigateToTab }) => {
    const [loading, setLoading] = useState(true);
    const [diamonds, setDiamonds] = useState([]);
    const [actionItems, setActionItems] = useState([]);
    // Dashboard section visibility preferences
    const { preferences, toggleSection, showAllSections, resetToDefaults, isSectionVisible, visibleSectionCount, totalSectionCount } = useDashboardPreferences('inventory');
    const [stats, setStats] = useState({
        total: 0,
        totalValue: 0,
        avgPricePerCarat: 0,
        avgStockDays: 0
    });
    const [shapeData, setShapeData] = useState([]);
    const [agingData, setAgingData] = useState([]);
    // Modal states
    const [showRepriceModal, setShowRepriceModal] = useState(false);
    const [showQuickActionModal, setShowQuickActionModal] = useState(false);
    const [quickActionType, setQuickActionType] = useState('promote');
    const [selectedDiamond, setSelectedDiamond] = useState(null);
    const [viewDiamond, setViewDiamond] = useState(null);
    useEffect(() => {
        fetchInventoryData();
    }, []);
    const fetchInventoryData = async () => {
        setLoading(true);
        try {
            let fetchedDiamonds = [];
            // Check if mock mode is enabled
            if (mockDataService.isMockMode()) {
                console.log('InventoryDashboard - Using mock diamonds');
                fetchedDiamonds = mockDataService.getDiamonds();
            }
            else {
                // Fetch from real API
                const response = await apiClient.get('diamonds-inventory?limit=1000');
                if (response.ok && response.data?.success && response.data.data?.diamonds) {
                    fetchedDiamonds = response.data.data.diamonds;
                }
                else {
                    console.error('Failed to fetch diamonds:', response.error);
                    notify.error('Error', 'Failed to load inventory data');
                    setLoading(false);
                    return;
                }
            }
            setDiamonds(fetchedDiamonds);
            // Calculate metrics
            calculateMetrics(fetchedDiamonds);
            calculateChartData(fetchedDiamonds);
            generateActionItems(fetchedDiamonds);
        }
        catch (error) {
            console.error('Error fetching inventory:', error);
            notify.error('Error', 'Failed to load inventory data');
        }
        finally {
            setLoading(false);
        }
    };
    // Helper to get diamond price (uses totalPrice or priceUSD as fallback)
    const getDiamondPrice = (d) => d.totalPrice || d.priceUSD || 0;
    const calculateMetrics = (diamonds) => {
        const total = diamonds.length;
        const totalValue = diamonds.reduce((sum, d) => sum + getDiamondPrice(d), 0);
        const avgPricePerCarat = total > 0
            ? diamonds.reduce((sum, d) => sum + (d.pricePerCarat || 0), 0) / total
            : 0;
        // Calculate avg stock days (days since import, using lastUpdated as fallback)
        const now = new Date();
        const diamondsWithDates = diamonds.filter(d => d.importedAt || d.lastUpdated);
        const avgStockDays = diamondsWithDates.length > 0
            ? Math.round(diamondsWithDates.reduce((sum, d) => {
                const dateField = d.importedAt || d.lastUpdated;
                const importDate = new Date(dateField);
                const days = Math.floor((now.getTime() - importDate.getTime()) / (1000 * 60 * 60 * 24));
                return sum + days;
            }, 0) / diamondsWithDates.length)
            : 0;
        setStats({
            total,
            totalValue,
            avgPricePerCarat: Math.round(avgPricePerCarat),
            avgStockDays
        });
    };
    const calculateChartData = (diamonds) => {
        // Shape distribution
        const shapeCounts = {};
        diamonds.forEach(d => {
            shapeCounts[d.shape] = (shapeCounts[d.shape] || 0) + 1;
        });
        const shapeChartData = Object.entries(shapeCounts)
            .map(([name, count]) => ({
            name,
            value: count,
            percentage: Math.round((count / diamonds.length) * 100)
        }))
            .sort((a, b) => b.value - a.value)
            .slice(0, 7); // Top 7 shapes
        setShapeData(shapeChartData);
        // Stock aging (days in inventory, using lastUpdated as fallback)
        const now = new Date();
        const aging = { current: 0, days30: 0, days60: 0, days90plus: 0 };
        diamonds.forEach(d => {
            const dateField = d.importedAt || d.lastUpdated;
            if (!dateField) {
                aging.current++; // Treat unknown dates as current/fresh
                return;
            }
            const importDate = new Date(dateField);
            const days = Math.floor((now.getTime() - importDate.getTime()) / (1000 * 60 * 60 * 24));
            if (days <= 30)
                aging.current++;
            else if (days <= 60)
                aging.days30++;
            else if (days <= 90)
                aging.days60++;
            else
                aging.days90plus++;
        });
        const total = diamonds.length || 1;
        setAgingData([
            { name: '0-30 days', value: aging.current, percentage: Math.round((aging.current / total) * 100) },
            { name: '31-60 days', value: aging.days30, percentage: Math.round((aging.days30 / total) * 100) },
            { name: '61-90 days', value: aging.days60, percentage: Math.round((aging.days60 / total) * 100) },
            { name: '90+ days', value: aging.days90plus, percentage: Math.round((aging.days90plus / total) * 100) }
        ]);
    };
    const generateActionItems = (diamonds) => {
        const actions = [];
        const now = new Date();
        // Critical: Slow-moving inventory (90+ days)
        // Uses importedAt or lastUpdated as fallback for tracking age
        diamonds.forEach(d => {
            const dateField = d.importedAt || d.lastUpdated;
            if (!dateField)
                return;
            const days = Math.floor((now.getTime() - new Date(dateField).getTime()) / (1000 * 60 * 60 * 24));
            const price = getDiamondPrice(d);
            if (days > 90) {
                actions.push({
                    type: 'slow-moving',
                    priority: 'critical',
                    title: `Slow-Moving: ${d.caratWeight}ct ${d.shape} ${d.color} ${d.clarity}`,
                    description: `${d.stockNumber || 'No stock#'} • ${days} days in inventory`,
                    amount: price,
                    daysInStock: days,
                    data: d,
                    action: 'review'
                });
            }
        });
        // High Priority: High-value items (>$50K)
        diamonds.filter(d => getDiamondPrice(d) > 50000).forEach(d => {
            const price = getDiamondPrice(d);
            actions.push({
                type: 'high-value',
                priority: 'high',
                title: `High-Value: ${d.caratWeight}ct ${d.shape} ${d.color} ${d.clarity}`,
                description: `${d.stockNumber || 'No stock#'} • ${d.lab} ${d.certificateNumber || 'No cert'} • Premium inventory`,
                amount: price,
                data: d,
                action: 'review'
            });
        });
        // Medium: Low-margin items (only if cost data is available)
        if (diamonds.some(d => d.costPerCarat && d.pricePerCarat)) {
            diamonds.forEach(d => {
                if (d.costPerCarat && d.pricePerCarat) {
                    const margin = ((d.pricePerCarat - d.costPerCarat) / d.costPerCarat) * 100;
                    const price = getDiamondPrice(d);
                    if (margin < 20 && margin > 0) {
                        actions.push({
                            type: 'low-margin',
                            priority: 'medium',
                            title: `Low Margin: ${d.caratWeight}ct ${d.shape} ${d.color} ${d.clarity}`,
                            description: `${d.stockNumber || 'No stock#'} • ${margin.toFixed(1)}% margin • Consider repricing`,
                            amount: price,
                            data: d,
                            action: 'review'
                        });
                    }
                }
            });
        }
        // Attention: Missing certificates
        diamonds.filter(d => !d.certificateNumber && getDiamondPrice(d) > 5000).forEach(d => {
            const price = getDiamondPrice(d);
            actions.push({
                type: 'missing-cert',
                priority: 'low',
                title: `Missing Cert: ${d.caratWeight}ct ${d.shape} ${d.color} ${d.clarity}`,
                description: `${d.stockNumber || 'No stock#'} • $${price.toLocaleString()} • Quality control needed`,
                amount: price,
                data: d,
                action: 'review'
            });
        });
        // Limit to top 50 actions (sorted by amount)
        setActionItems(actions.sort((a, b) => b.amount - a.amount).slice(0, 50));
    };
    const handleAction = (action, data) => {
        switch (action) {
            case 'review':
            case 'view':
                // Open detail modal inline (no navigation!)
                setViewDiamond(data);
                break;
            case 'reprice':
                setSelectedDiamond(data);
                setShowRepriceModal(true);
                break;
            case 'promote':
                setSelectedDiamond(data);
                setQuickActionType('promote');
                setShowQuickActionModal(true);
                break;
            case 'verify':
                setSelectedDiamond(data);
                setQuickActionType('verify');
                setShowQuickActionModal(true);
                break;
            case 'analyze':
                setSelectedDiamond(data);
                setQuickActionType('analyze');
                setShowQuickActionModal(true);
                break;
            case 'order-cert':
                setSelectedDiamond(data);
                setQuickActionType('order-cert');
                setShowQuickActionModal(true);
                break;
            default:
                break;
        }
    };
    const handleModalSuccess = () => {
        // Refresh dashboard data after action
        fetchInventoryData();
    };
    // Group actions by priority
    const criticalItems = actionItems.filter(a => a.priority === 'critical');
    const highItems = actionItems.filter(a => a.priority === 'high');
    const mediumItems = actionItems.filter(a => a.priority === 'medium');
    const lowItems = actionItems.filter(a => a.priority === 'low');
    if (loading) {
        return (_jsx(Container, { children: _jsx("div", { style: { textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }, children: "Loading inventory data..." }) }));
    }
    return (_jsxs(Container, { children: [_jsxs(HeaderActions, { children: [_jsxs(Button, { "$primary": true, onClick: fetchInventoryData, disabled: loading, children: [_jsx(FaSync, {}), " ", loading ? 'Loading...' : 'Refresh'] }), _jsx(DashboardSettingsPanel, { sectionVisibility: preferences.sectionVisibility, sections: INVENTORY_DASHBOARD_SECTIONS, onToggleSection: toggleSection, onShowAll: showAllSections, onReset: resetToDefaults, visibleCount: visibleSectionCount, totalCount: totalSectionCount })] }), isSectionVisible('stats') && (_jsxs(StatsGrid, { id: "tour-inventory-stats", children: [_jsxs(StatCard, { "$color": "#8b5cf6", children: [_jsx(StatLabel, { children: "Total Inventory" }), _jsx(StatValue, { children: stats.total.toLocaleString() }), _jsx(StatSubtext, { children: "diamonds in stock" })] }), _jsxs(StatCard, { "$color": "#10b981", children: [_jsx(StatLabel, { children: "Total Value" }), _jsxs(StatValue, { children: ["$", (stats.totalValue / 1000000).toFixed(2), "M"] }), _jsx(StatSubtext, { children: "inventory valuation" })] }), _jsxs(StatCard, { "$color": "#3b82f6", children: [_jsx(StatLabel, { children: "Avg Price/Carat" }), _jsxs(StatValue, { children: ["$", stats.avgPricePerCarat.toLocaleString()] }), _jsx(StatSubtext, { children: "quality indicator" })] }), _jsxs(StatCard, { "$color": "#f59e0b", children: [_jsx(StatLabel, { children: "Avg Stock Days" }), _jsx(StatValue, { children: stats.avgStockDays }), _jsx(StatSubtext, { children: "turnover rate" })] })] })), isSectionVisible('charts') && shapeData.length > 0 && (_jsxs(ChartsContainer, { id: "tour-inventory-charts", children: [_jsxs(ChartCard, { children: [_jsx(ChartTitle, { children: "Inventory by Shape" }), _jsx(ResponsiveContainer, { width: "100%", height: 250, children: _jsxs(PieChart, { children: [_jsx(Pie, { data: shapeData, cx: "50%", cy: "50%", labelLine: false, label: ({ name, percentage }) => percentage > 5 ? `${name} ${percentage}%` : '', outerRadius: 80, fill: "#8884d8", dataKey: "value", children: shapeData.map((entry, index) => (_jsx(Cell, { fill: SHAPE_COLORS[index % SHAPE_COLORS.length] }, `cell-${index}`))) }), _jsx(Tooltip, { formatter: (value, name, props) => [`${value} diamonds (${props.payload.percentage}%)`, name] }), _jsx(Legend, {})] }) })] }), _jsxs(ChartCard, { children: [_jsx(ChartTitle, { children: "Stock Aging (Days in Inventory)" }), _jsx(ResponsiveContainer, { width: "100%", height: 250, children: _jsxs(BarChart, { data: agingData, children: [_jsx(XAxis, { dataKey: "name", stroke: "var(--text-tertiary)", style: { fontSize: '0.75rem' } }), _jsx(YAxis, { stroke: "var(--text-tertiary)", style: { fontSize: '0.75rem' } }), _jsx(Tooltip, { formatter: (value, name, props) => [`${value} diamonds (${props.payload.percentage}%)`, name] }), _jsx(Bar, { dataKey: "value", radius: [8, 8, 0, 0], children: agingData.map((entry, index) => (_jsx(Cell, { fill: AGING_COLORS[index] }, `cell-${index}`))) })] }) })] })] })), actionItems.length > 0 ? (_jsxs("div", { id: "tour-inventory-actions", children: [isSectionVisible('criticalActions') && criticalItems.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#dc2626", children: [_jsxs(SectionTitle, { children: [_jsx(FaExclamationCircle, {}), " Slow-Moving Inventory (90+ Days)"] }), _jsx(SectionBadge, { "$color": "#dc2626", children: criticalItems.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Days in Stock" }), _jsx(Th, { children: "Value" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: criticalItems.slice(0, 5).map((item) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs("strong", { children: [item.data.caratWeight, "ct ", item.data.shape, " ", item.data.color, " ", item.data.clarity] }) }), _jsx(Td, { style: { fontFamily: 'monospace' }, children: item.data.stockNumber || '-' }), _jsx(Td, { children: _jsxs(DaysBadge, { "$days": item.daysInStock || 0, children: [item.daysInStock, " days"] }) }), _jsx(Td, { children: _jsxs(PriceCell, { "$warning": true, children: ["$", item.amount.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsxs(ActionButton, { "$variant": "primary", onClick: () => handleAction('reprice', item.data), children: [_jsx(FaDollarSign, {}), " Reprice"] }), _jsx(ActionButton, { onClick: () => handleAction('promote', item.data), children: _jsx(FaBullhorn, {}) }), _jsx(ActionButton, { onClick: () => handleAction('view', item.data), children: _jsx(FaEye, {}) })] }) })] }, item.data.id))) })] }) }), criticalItems.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('diamonds', { filterType: 'slow-moving', minDaysInStock: 90 }), children: ["View All ", criticalItems.length, " Slow-Moving Items \u2192"] }) }))] })), isSectionVisible('highPriorityActions') && highItems.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#f59e0b", children: [_jsxs(SectionTitle, { children: [_jsx(FaBolt, {}), " High-Value Items ($50K+)"] }), _jsx(SectionBadge, { "$color": "#f59e0b", children: highItems.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Certificate" }), _jsx(Th, { children: "Value" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: highItems.slice(0, 5).map((item) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs("strong", { children: [item.data.caratWeight, "ct ", item.data.shape, " ", item.data.color, " ", item.data.clarity] }) }), _jsx(Td, { style: { fontFamily: 'monospace' }, children: item.data.stockNumber || '-' }), _jsxs(Td, { style: { fontSize: '0.8rem', color: 'var(--text-secondary)' }, children: [item.data.lab, " ", item.data.certificateNumber || 'No cert'] }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", item.amount.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsxs(ActionButton, { "$variant": "primary", onClick: () => handleAction('verify', item.data), children: [_jsx(FaShieldAlt, {}), " Verify"] }), _jsx(ActionButton, { onClick: () => handleAction('promote', item.data), children: _jsx(FaBullhorn, {}) }), _jsx(ActionButton, { onClick: () => handleAction('view', item.data), children: _jsx(FaEye, {}) })] }) })] }, item.data.id))) })] }) }), highItems.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('diamonds', { filterType: 'high-value', minPrice: 50000 }), children: ["View All ", highItems.length, " High-Value Items \u2192"] }) }))] })), isSectionVisible('mediumActions') && mediumItems.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#10b981", children: [_jsxs(SectionTitle, { children: [_jsx(FaClock, {}), " Low-Margin Items (<20%)"] }), _jsx(SectionBadge, { "$color": "#10b981", children: mediumItems.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Margin" }), _jsx(Th, { children: "Value" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: mediumItems.slice(0, 5).map((item) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs("strong", { children: [item.data.caratWeight, "ct ", item.data.shape, " ", item.data.color, " ", item.data.clarity] }) }), _jsx(Td, { style: { fontFamily: 'monospace' }, children: item.data.stockNumber || '-' }), _jsx(Td, { style: { fontSize: '0.8rem', color: '#f59e0b' }, children: item.description.match(/(\d+\.?\d*)%/)?.[0] || 'Low' }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", item.amount.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsxs(ActionButton, { "$variant": "primary", onClick: () => handleAction('reprice', item.data), children: [_jsx(FaDollarSign, {}), " Reprice"] }), _jsx(ActionButton, { onClick: () => handleAction('analyze', item.data), children: _jsx(FaChartLine, {}) }), _jsx(ActionButton, { onClick: () => handleAction('view', item.data), children: _jsx(FaEye, {}) })] }) })] }, item.data.id))) })] }) }), mediumItems.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('diamonds', { filterType: 'low-margin' }), children: ["View All ", mediumItems.length, " Low-Margin Items \u2192"] }) }))] })), isSectionVisible('pendingApprovals') && lowItems.length > 0 && (_jsxs(Section, { children: [_jsxs(SectionHeader, { "$color": "#3b82f6", children: [_jsxs(SectionTitle, { children: [_jsx(FaCertificate, {}), " Missing Certificates"] }), _jsx(SectionBadge, { "$color": "#3b82f6", children: lowItems.length })] }), _jsx(TableContainer, { children: _jsxs(Table, { children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx(Th, { children: "Diamond" }), _jsx(Th, { children: "Stock #" }), _jsx(Th, { children: "Lab" }), _jsx(Th, { children: "Value" }), _jsx(Th, { children: "Actions" })] }) }), _jsx("tbody", { children: lowItems.slice(0, 5).map((item) => (_jsxs(Tr, { children: [_jsx(Td, { children: _jsxs("strong", { children: [item.data.caratWeight, "ct ", item.data.shape, " ", item.data.color, " ", item.data.clarity] }) }), _jsx(Td, { style: { fontFamily: 'monospace' }, children: item.data.stockNumber || '-' }), _jsx(Td, { style: { fontSize: '0.8rem', color: 'var(--text-tertiary)' }, children: item.data.lab || 'Unknown' }), _jsx(Td, { children: _jsxs(PriceCell, { children: ["$", item.amount.toLocaleString()] }) }), _jsx(Td, { children: _jsxs(ActionButtons, { children: [_jsxs(ActionButton, { "$variant": "primary", onClick: () => handleAction('order-cert', item.data), children: [_jsx(FaCertificate, {}), " Order"] }), _jsx(ActionButton, { onClick: () => handleAction('view', item.data), children: _jsx(FaEye, {}) })] }) })] }, item.data.id))) })] }) }), lowItems.length > 5 && (_jsx("div", { style: { textAlign: 'center', marginTop: '0.75rem' }, children: _jsxs(Button, { onClick: () => onNavigateToTab('diamonds', { filterType: 'missing-cert' }), children: ["View All ", lowItems.length, " Items Needing Certificates \u2192"] }) }))] }))] })) : diamonds.length > 0 ? (_jsxs(AllCaughtUp, { children: [_jsx(FaCheckCircle, {}), _jsx("h3", { children: "All Caught Up!" }), _jsx("p", { children: "No priority actions needed. Your inventory is in good shape." })] })) : (_jsxs(EmptyState, { children: [_jsx(FaGem, {}), _jsx("h3", { children: "No Inventory Data Yet" }), _jsx("p", { children: "Import diamonds via the Import/Export tab to see analytics and insights here." }), _jsx("p", { style: { marginTop: '1rem', fontSize: '0.875rem' }, children: "Once you have inventory, you'll see:" }), _jsxs("ul", { style: {
                            listStyle: 'none',
                            padding: 0,
                            margin: '0.5rem 0 0 0',
                            fontSize: '0.875rem',
                            color: 'var(--text-secondary)'
                        }, children: [_jsx("li", { children: "\u2022 Stock distribution and aging analytics" }), _jsx("li", { children: "\u2022 Slow-moving inventory alerts (90+ days)" }), _jsx("li", { children: "\u2022 High-value item tracking ($50K+)" }), _jsx("li", { children: "\u2022 Pricing and margin analysis" }), _jsx("li", { children: "\u2022 Certificate verification status" })] })] })), _jsx(RepriceDiamondModal, { isOpen: showRepriceModal, onClose: () => {
                    setShowRepriceModal(false);
                    setSelectedDiamond(null);
                }, diamond: selectedDiamond, onSuccess: handleModalSuccess }), _jsx(QuickActionModal, { isOpen: showQuickActionModal, onClose: () => {
                    setShowQuickActionModal(false);
                    setSelectedDiamond(null);
                }, diamond: selectedDiamond, actionType: quickActionType, onSuccess: handleModalSuccess }), viewDiamond && (_jsx(DiamondDetailModal, { diamond: viewDiamond, onClose: () => setViewDiamond(null) }))] }));
};
export default InventoryDashboardTab;
